<template>
    <wrapper-features-templates-one-column
    buttonFullWidth
    :hideWhiteBoxOnLoad="showLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
	:showFooter="!showLoader && !applyErrorState && getMobileAppState && getTpasTypesList.length"
	:footerButtonsParams="footerButtonsData"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <selectors-wrapper
        v-if="!showLoader && !applyErrorState && getTpasTypesList.length"
        :applyMobileHeaderProductSlider="getMobileAppState"
        :productsList="tpasList"
        @updateSelectedProductIndex="mobileSelectedIndex = $event"/>
    </wrapper-features-templates-one-column>

    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import SelectorsWrapper from '@/components/generic/cards/productSelector/SelectorsWrapper'
import ToastsGeneric from '@/components/generic/toasts/Generic'
/* Vue */
import { ref, computed } from 'vue'

/* Assets & Helpers */
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {	requestListTPAsTypesAvailable, validatesNIFForTPAsRequest } = mapActions(moduleMapper.COMPANY_PAYMENTS_REQUEST_TPA)
const {	getTpasTypesList } = mapGetters(moduleMapper.COMPANY_PAYMENTS_REQUEST_TPA)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const applyErrorState = ref(false)
const tpasList = ref([])
const mobileSelectedIndex = ref(0)

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
	return [{
		label: t('companyPayments.requestTPA.tpasList.selectBtnText'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		callbackFunction: () => {
            const selectedProduct = getTpasTypesList.value[mobileSelectedIndex.value].id
            tpaRequestHandler(selectedProduct)
        }
	}]
})

/*************************************************
*                                                *
*                Initial Requets                 *
*                                                *
*************************************************/
const initialRequests = async () => {
    applyErrorState.value = false
	showLoader.value = true

	try {
        await requestListTPAsTypesAvailable()

        if (getTpasTypesList.value.length) {
            /* Setting TPAs List */
            getTpasTypesList.value.forEach(tpa => {
                return tpasList.value.push({
                    ...tpa,
                    id: tpa.id,
                    identifier: tpa.id,
                    title: tpa.shortDescription,
                    descriptionClass: 'bold',
                    imageUrl: tpa.tpaImageUrl,
                    infoSheetUrlBtnText: t('companyPayments.requestTPA.tpasList.infoSheetUrlBtnText'),
                    infoSheetCustomSuccessMessage: t('companyPayments.requestTPA.tpasList.downloadInfoSheetSuccessMessage'),
                    secondBtnText: t('companyPayments.requestTPA.tpasList.selectBtnText'),
                    list: tpa.features,
                    secondBtnClicked: () => tpaRequestHandler(tpa.id)
                })
            })
        }
		showLoader.value = false
	} catch (error) {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequests)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*             Processing Handlers                *
*                                                *
*************************************************/
const tpaTypeId = ref({})
const tpaSelected = computed(() => {
    return getTpasTypesList.value.find(tpa => tpa.shortDescription)
})

const tpaRequestHandler = async (tpaId) => {
	showOverlayLoader.value = true
    /* For retry purposes we need to save it in the view */
    tpaTypeId.value = tpaId || tpaTypeId.value

    try {
        const res = await validatesNIFForTPAsRequest()

        switch (getTpasTypesList.value.find(tpa => tpa.id === tpaId).typeCode) {
            case 'TPA_BAI':
                navigateByName(routeList.COMPANY_PAYMENTS_REQUEST_TPA_BAI_NAME, {
                    params: {
                        productName: tpaSelected.value.shortDescription,
                        hasNifValidated: res.data.data.hasNifValidated,
                        tpaTypeId: tpaTypeId.value,
                        infoSheetUrl: tpaSelected.value.infoSheetUrl,
                        termsAndConditionsUrl: tpaSelected.value.termsAndConditionsUrl
                    }
                })
                break
        }

        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, tpaRequestHandler)
    }
}

initialRequests()
</script>

<style lang="scss" scoped>
</style>
